import { useDeferredValue, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  Badge,
  Breadcrumb,
  Button,
  Card,
  Col,
  Drawer,
  Empty,
  Form,
  Input,
  List,
  Modal,
  Radio,
  Row,
  Select,
  Segmented,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  AppstoreOutlined,
  CopyOutlined,
  FolderOpenOutlined,
  DeleteOutlined,
  EditOutlined,
  SaveOutlined,
  SearchOutlined,
  SyncOutlined,
  TeamOutlined,
} from '@ant-design/icons'

import { useAuthStore } from '@/store/auth'
import { useAccessGroups, useGroupEntries } from '@/hooks/useAccessGroups'
import { useOrganizations } from '@/hooks/useUsers'
import * as accessGroupsApi from '@/api/accessGroups'
import type {
  AccessGroupEntry,
  AccessGroupResponse,
  DashboardAclTemplateDetail,
  DashboardAclTemplateEntry,
  DashboardAclTemplateSummary,
} from '@/api/accessGroups'
import { isOrgMembershipRoleKey } from '@/utils/accessGroups'
import { parseCsvParam, setQueryParam } from '@/utils/searchParams'

const { Title, Paragraph, Text } = Typography

type ApplyMode = 'merge' | 'replace'
type SourceMode = 'group' | 'template'
type TemplateModalMode = 'create' | 'edit'

interface PreviewRow {
  targetGroupId: string
  targetGroupName: string
  currentCount: number
  sourceCount: number
  missingCount: number
  extraCount: number
}

interface EntryDiffItem {
  datalens_entry_id: string
  entry_type: string
  label: string
}

interface OrgCoverageRow {
  orgId: string
  orgLabel: string
  matchingGroups: number
}

interface WorkspaceEntry {
  id: string
  datalens_entry_id: string
  entry_type: string
  entry_title: string | null
  workbook_title: string | null
  collection_title: string | null
}

function isNonAclGroup(group: AccessGroupResponse): boolean {
  return isOrgMembershipRoleKey(group.zitadel_role_key) || group.zitadel_role_key === 'medcifra_admin'
}

function extractRoleFamily(roleKey: string | null | undefined): string | null {
  if (!roleKey || !roleKey.includes('-')) {
    return null
  }
  const family = roleKey.split('-').slice(1).join('-')
  return family || null
}

function formatEntryLabel(entry: AccessGroupEntry): string {
  return entry.entry_title || entry.workbook_title || entry.collection_title || entry.datalens_entry_id
}

function formatWorkspaceEntryLabel(entry: WorkspaceEntry): string {
  return entry.entry_title || entry.workbook_title || entry.collection_title || entry.datalens_entry_id
}

function normalizeTemplateEntry(entry: DashboardAclTemplateEntry): WorkspaceEntry {
  return {
    id: entry.datalens_entry_id,
    datalens_entry_id: entry.datalens_entry_id,
    entry_type: entry.entry_type,
    entry_title: entry.entry_title,
    workbook_title: null,
    collection_title: null,
  }
}

function normalizeGroupEntry(entry: AccessGroupEntry): WorkspaceEntry {
  return {
    id: entry.id,
    datalens_entry_id: entry.datalens_entry_id,
    entry_type: entry.entry_type,
    entry_title: entry.entry_title,
    workbook_title: entry.workbook_title,
    collection_title: entry.collection_title,
  }
}

export default function DashboardAccessWorkspacePage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const authUser = useAuthStore((s) => s.user)
  const isSuperadmin = authUser?.role === 'superadmin'
  const [templateForm] = Form.useForm<{ name: string; description?: string }>()

  const initialSourceMode = searchParams.get('source') === 'template' ? 'template' : 'group'

  const [orgFilter, setOrgFilter] = useState<string | undefined>(
    isSuperadmin ? (searchParams.get('org') ?? undefined) : (authUser?.orgId ?? undefined)
  )
  const [search, setSearch] = useState(() => searchParams.get('q') ?? '')
  const [sourceMode, setSourceMode] = useState<SourceMode>(initialSourceMode)
  const [sourceGroupId, setSourceGroupId] = useState<string | null>(() => searchParams.get('group'))
  const [sourceTemplateId, setSourceTemplateId] = useState<string | null>(() => searchParams.get('template'))
  const [targetOrgIds, setTargetOrgIds] = useState<string[]>(() => parseCsvParam(searchParams.get('targetOrgs')))
  const [targetGroupIds, setTargetGroupIds] = useState<string[]>(() => parseCsvParam(searchParams.get('targets')))
  const [applyMode, setApplyMode] = useState<ApplyMode>(
    searchParams.get('apply') === 'replace' ? 'replace' : 'merge'
  )
  const [isApplying, setIsApplying] = useState(false)
  const [detailsGroupId, setDetailsGroupId] = useState<string | null>(() => searchParams.get('details'))
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false)
  const [isSavingTemplate, setIsSavingTemplate] = useState(false)
  const [templateModalMode, setTemplateModalMode] = useState<TemplateModalMode>('create')

  const deferredSearch = useDeferredValue(search)

  const { data: organizations } = useOrganizations()
  const { data: groups, isLoading: groupsLoading } = useAccessGroups(orgFilter)
  const { data: templates = [], isLoading: templatesLoading } = useQuery<DashboardAclTemplateSummary[]>({
    queryKey: ['dashboard-acl-templates', orgFilter ?? 'all'],
    queryFn: () => accessGroupsApi.fetchDashboardAclTemplates(orgFilter),
  })
  const filteredGroups = useMemo(() => {
    const items = (groups || []).filter((group) => group.is_active && !isNonAclGroup(group))
    if (!deferredSearch) return items
    const needle = deferredSearch.toLowerCase()
    return items.filter((group) =>
      `${group.name} ${group.zitadel_role_key} ${group.description || ''}`.toLowerCase().includes(needle)
    )
  }, [groups, deferredSearch])
  const filteredTemplates = useMemo(() => {
    if (!deferredSearch) return templates
    const needle = deferredSearch.toLowerCase()
    return templates.filter((template) =>
      `${template.name} ${template.description || ''}`.toLowerCase().includes(needle)
    )
  }, [templates, deferredSearch])

  useEffect(() => {
    const next = new URLSearchParams(searchParams)
    setQueryParam(next, 'q', search || null)
    setQueryParam(next, 'org', isSuperadmin ? (orgFilter ?? null) : null)
    setQueryParam(next, 'source', sourceMode)
    setQueryParam(next, 'group', sourceMode === 'group' ? sourceGroupId : null)
    setQueryParam(next, 'template', sourceMode === 'template' ? sourceTemplateId : null)
    setQueryParam(next, 'targetOrgs', targetOrgIds.length ? targetOrgIds.join(',') : null)
    setQueryParam(next, 'targets', targetGroupIds.length ? targetGroupIds.join(',') : null)
    setQueryParam(next, 'apply', applyMode)
    setQueryParam(next, 'details', detailsGroupId)
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true })
    }
  }, [
    applyMode,
    detailsGroupId,
    isSuperadmin,
    orgFilter,
    search,
    searchParams,
    setSearchParams,
    sourceGroupId,
    sourceMode,
    sourceTemplateId,
    targetGroupIds,
    targetOrgIds,
  ])

  const sourceGroup = filteredGroups.find((group) => group.id === sourceGroupId) ?? null
  const sourceTemplate = filteredTemplates.find((template) => template.id === sourceTemplateId) ?? null
  const targetGroups = filteredGroups.filter((group) => targetGroupIds.includes(group.id))
  const sourceTemplateOriginGroup =
    (groups || []).find((group) => group.id === sourceTemplate?.source_group_id) ?? null

  const { data: sourceGroupEntries = [], isLoading: sourceGroupEntriesLoading } = useGroupEntries(
    sourceMode === 'group' ? (sourceGroupId ?? '') : ''
  )
  const {
    data: sourceTemplateDetail,
    isLoading: sourceTemplateLoading,
  } = useQuery<DashboardAclTemplateDetail>({
    queryKey: ['dashboard-acl-template', sourceTemplateId],
    queryFn: () => accessGroupsApi.fetchDashboardAclTemplate(sourceTemplateId!),
    enabled: sourceMode === 'template' && !!sourceTemplateId,
  })
  const sourceEntries = useMemo<WorkspaceEntry[]>(
    () =>
      sourceMode === 'template'
        ? (sourceTemplateDetail?.entries || []).map(normalizeTemplateEntry)
        : sourceGroupEntries.map(normalizeGroupEntry),
    [sourceGroupEntries, sourceMode, sourceTemplateDetail]
  )
  const sourceEntriesLoading = sourceMode === 'template' ? sourceTemplateLoading : sourceGroupEntriesLoading
  const sourceLabel = sourceMode === 'template' ? sourceTemplate?.name : sourceGroup?.name
  const sourceSubtitle = sourceMode === 'template'
    ? sourceTemplate?.description || 'Сохраненный шаблон видимости'
    : sourceGroup?.zitadel_role_key
  const sourceRoleKey = sourceMode === 'template' ? sourceTemplateOriginGroup?.zitadel_role_key : sourceGroup?.zitadel_role_key
  const sourceRoleFamily = extractRoleFamily(sourceRoleKey)
  const availableTargetGroups = useMemo(() => {
    if (targetOrgIds.length === 0) {
      return filteredGroups
    }
    return filteredGroups.filter((group) => group.org_id && targetOrgIds.includes(group.org_id))
  }, [filteredGroups, targetOrgIds])

  const targetEntryQueries = useQueries({
    queries: targetGroupIds.map((groupId) => ({
      queryKey: ['access-group-entries', groupId],
      queryFn: () => accessGroupsApi.fetchEntries(groupId),
      enabled: !!groupId,
    })),
  })

  const targetEntriesMap = useMemo(() => {
    const map = new Map<string, AccessGroupEntry[]>()
    targetGroupIds.forEach((groupId, index) => {
      map.set(groupId, (targetEntryQueries[index]?.data as AccessGroupEntry[] | undefined) || [])
    })
    return map
  }, [targetEntryQueries, targetGroupIds])

  const sourceEntryIds = useMemo(() => new Set(sourceEntries.map((entry) => entry.datalens_entry_id)), [sourceEntries])

  const previewRows = useMemo<PreviewRow[]>(() => {
    return targetGroups.map((group) => {
      const currentEntries = targetEntriesMap.get(group.id) || []
      const currentIds = new Set(currentEntries.map((entry) => entry.datalens_entry_id))
      const missingCount = sourceEntries.filter((entry) => !currentIds.has(entry.datalens_entry_id)).length
      const extraCount = currentEntries.filter((entry) => !sourceEntryIds.has(entry.datalens_entry_id)).length

      return {
        targetGroupId: group.id,
        targetGroupName: group.name,
        currentCount: currentEntries.length,
        sourceCount: sourceEntries.length,
        missingCount,
        extraCount,
      }
    })
  }, [sourceEntries, sourceEntryIds, targetEntriesMap, targetGroups])

  const selectedPreviewGroup = targetGroups.find((group) => group.id === detailsGroupId) ?? null
  const selectedPreviewDiff = useMemo(() => {
    const selectedPreviewEntries = selectedPreviewGroup ? (targetEntriesMap.get(selectedPreviewGroup.id) || []) : []
    const currentById = new Map(selectedPreviewEntries.map((entry) => [entry.datalens_entry_id, entry]))
    const missing: EntryDiffItem[] = sourceEntries
      .filter((entry) => !currentById.has(entry.datalens_entry_id))
      .map((entry) => ({
        datalens_entry_id: entry.datalens_entry_id,
        entry_type: entry.entry_type,
        label: formatWorkspaceEntryLabel(entry),
      }))

    const extra: EntryDiffItem[] = selectedPreviewEntries
      .filter((entry) => !sourceEntryIds.has(entry.datalens_entry_id))
      .map((entry) => ({
        datalens_entry_id: entry.datalens_entry_id,
        entry_type: entry.entry_type,
        label: formatEntryLabel(entry),
      }))

    return { missing, extra }
  }, [selectedPreviewGroup, sourceEntries, sourceEntryIds, targetEntriesMap])

  const orgOptions = useMemo(
    () =>
      (organizations || []).map((org) => ({
        value: org.id,
        label: `${org.code} — ${org.name}`,
      })),
    [organizations]
  )
  const workspaceOrgId = isSuperadmin ? orgFilter : (authUser?.orgId ?? undefined)
  const workspaceOrgLabel = workspaceOrgId
    ? orgOptions.find((org) => org.value === workspaceOrgId)?.label || workspaceOrgId
    : null
  const biCenterUrl = workspaceOrgId ? `/admin/bi-workspace?org=${workspaceOrgId}` : '/admin/bi-workspace'

  const targetOptions = useMemo(
    () =>
      availableTargetGroups
        .filter((group) => sourceMode !== 'group' || group.id !== sourceGroupId)
        .map((group) => ({
          value: group.id,
          label: `${group.name} · ${group.zitadel_role_key}`,
        })),
    [availableTargetGroups, sourceGroupId, sourceMode]
  )
  const matchingFamilyTargets = useMemo(() => {
    if (!sourceRoleFamily) {
      return []
    }
    return availableTargetGroups.filter((group) => {
      if (sourceMode === 'group' && group.id === sourceGroupId) {
        return false
      }
      return extractRoleFamily(group.zitadel_role_key) === sourceRoleFamily
    })
  }, [availableTargetGroups, sourceGroupId, sourceMode, sourceRoleFamily])
  const orgCoverageRows = useMemo<OrgCoverageRow[]>(() => {
    if (!sourceRoleFamily) {
      return []
    }
    const byOrg = new Map<string, number>()
    for (const group of matchingFamilyTargets) {
      if (!group.org_id) continue
      byOrg.set(group.org_id, (byOrg.get(group.org_id) || 0) + 1)
    }
    return (organizations || [])
      .filter((org) => byOrg.has(org.id))
      .map((org) => ({
        orgId: org.id,
        orgLabel: `${org.code} — ${org.name}`,
        matchingGroups: byOrg.get(org.id) || 0,
      }))
      .sort((a, b) => a.orgLabel.localeCompare(b.orgLabel, 'ru'))
  }, [matchingFamilyTargets, organizations, sourceRoleFamily])

  const handleCreateTemplate = async (values: { name: string; description?: string }) => {
    if (!sourceGroup) {
      return
    }
    setIsSavingTemplate(true)
    try {
      const created = await accessGroupsApi.createDashboardAclTemplateFromGroup(sourceGroup.id, {
        name: values.name,
        description: values.description || null,
      })
      await queryClient.invalidateQueries({ queryKey: ['dashboard-acl-templates'] })
      await queryClient.invalidateQueries({ queryKey: ['dashboard-acl-template'] })
      setSourceMode('template')
      setSourceTemplateId(created.id)
      setTargetGroupIds([])
      setIsTemplateModalOpen(false)
      templateForm.resetFields()
      message.success('Шаблон видимости сохранен')
    } catch (error: unknown) {
      const detail =
        typeof error === 'object' &&
        error !== null &&
        'response' in error &&
        typeof (error as { response?: unknown }).response === 'object' &&
        (error as { response?: { data?: { detail?: string } } }).response?.data?.detail
      message.error(detail || 'Ошибка сохранения шаблона видимости')
    } finally {
      setIsSavingTemplate(false)
    }
  }

  const handleUpdateTemplate = async (values: { name: string; description?: string }) => {
    if (!sourceTemplateId) {
      return
    }
    setIsSavingTemplate(true)
    try {
      await accessGroupsApi.updateDashboardAclTemplate(sourceTemplateId, {
        name: values.name,
        description: values.description || null,
      })
      await queryClient.invalidateQueries({ queryKey: ['dashboard-acl-templates'] })
      await queryClient.invalidateQueries({ queryKey: ['dashboard-acl-template', sourceTemplateId] })
      setIsTemplateModalOpen(false)
      templateForm.resetFields()
      message.success('Шаблон видимости обновлен')
    } catch (error: unknown) {
      const detail =
        typeof error === 'object' &&
        error !== null &&
        'response' in error &&
        typeof (error as { response?: unknown }).response === 'object' &&
        (error as { response?: { data?: { detail?: string } } }).response?.data?.detail
      message.error(detail || 'Ошибка обновления шаблона видимости')
    } finally {
      setIsSavingTemplate(false)
    }
  }

  const handleDeleteTemplate = async () => {
    if (!sourceTemplateId || !sourceTemplate) {
      return
    }
    Modal.confirm({
      title: 'Удалить шаблон видимости?',
      content: `Шаблон "${sourceTemplate.name}" будет удален. Существующие группы видимости это не затронет.`,
      okText: 'Удалить',
      okButtonProps: { danger: true },
      cancelText: 'Отмена',
      onOk: async () => {
        await accessGroupsApi.deleteDashboardAclTemplate(sourceTemplateId)
        await queryClient.invalidateQueries({ queryKey: ['dashboard-acl-templates'] })
        setSourceTemplateId(null)
        setTargetGroupIds([])
        message.success('Шаблон видимости удален')
      },
    })
  }

  const sourceColumns: ColumnsType<WorkspaceEntry> = [
    {
      title: 'Дашборд',
      key: 'entry',
      render: (_value, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{formatWorkspaceEntryLabel(record)}</Text>
          <Text type="secondary" code>{record.datalens_entry_id}</Text>
        </Space>
      ),
    },
    {
      title: 'Тип',
      dataIndex: 'entry_type',
      key: 'entry_type',
      width: 120,
      render: (value: string) => <Tag>{value}</Tag>,
    },
  ]

  const previewColumns: ColumnsType<PreviewRow> = [
    {
      title: 'Целевая группа',
      dataIndex: 'targetGroupName',
      key: 'targetGroupName',
      render: (value: string) => <Text strong>{value}</Text>,
    },
    {
      title: 'Текущий набор',
      dataIndex: 'currentCount',
      key: 'currentCount',
      width: 140,
    },
    {
      title: 'В источнике',
      dataIndex: 'sourceCount',
      key: 'sourceCount',
      width: 120,
    },
    {
      title: 'Добавится',
      dataIndex: 'missingCount',
      key: 'missingCount',
      width: 120,
      render: (value: number) => <Badge count={value} showZero color="#1677ff" />,
    },
    {
      title: 'Будет удалено',
      dataIndex: 'extraCount',
      key: 'extraCount',
      width: 140,
      render: (value: number) => <Badge count={value} showZero color="#fa8c16" />,
    },
    {
      title: 'Детали',
      key: 'details',
      width: 120,
      render: (_value, record) => (
        <Button type="link" onClick={() => setDetailsGroupId(record.targetGroupId)}>
          Открыть
        </Button>
      ),
    },
  ]

  const detailColumns: ColumnsType<EntryDiffItem> = [
    {
      title: 'Дашборд',
      key: 'label',
      render: (_value, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.label}</Text>
          <Text type="secondary" code>{record.datalens_entry_id}</Text>
        </Space>
      ),
    },
    {
      title: 'Тип',
      dataIndex: 'entry_type',
      key: 'entry_type',
      width: 100,
      render: (value: string) => <Tag>{value}</Tag>,
    },
  ]
  const orgCoverageColumns: ColumnsType<OrgCoverageRow> = [
    {
      title: 'Организация',
      dataIndex: 'orgLabel',
      key: 'orgLabel',
      render: (value: string) => <Text>{value}</Text>,
    },
    {
      title: 'Групп этого типа',
      dataIndex: 'matchingGroups',
      key: 'matchingGroups',
      width: 160,
      render: (value: number) => <Badge count={value} showZero color="#1677ff" />,
    },
  ]

  const handleApply = async () => {
    if (!sourceLabel || targetGroups.length === 0) {
      return
    }

    setIsApplying(true)
    try {
      for (const targetGroup of targetGroups) {
        const currentEntries = targetEntriesMap.get(targetGroup.id) || []
        const currentById = new Map(currentEntries.map((entry) => [entry.datalens_entry_id, entry]))

        for (const entry of sourceEntries) {
          if (!currentById.has(entry.datalens_entry_id)) {
            await accessGroupsApi.addEntry(targetGroup.id, {
              datalens_entry_id: entry.datalens_entry_id,
              entry_type: entry.entry_type,
              entry_title: entry.entry_title,
            })
          }
        }

        if (applyMode === 'replace') {
          for (const entry of currentEntries) {
            if (!sourceEntryIds.has(entry.datalens_entry_id)) {
              await accessGroupsApi.deleteEntry(targetGroup.id, entry.id)
            }
          }
        }

        await queryClient.invalidateQueries({ queryKey: ['access-group-entries', targetGroup.id] })
      }

              message.success(
        applyMode === 'merge'
          ? 'Пропущенные дашборды добавлены в целевые группы'
          : 'Целевые группы приведены к составу исходной группы'
      )
    } catch (error: unknown) {
      const detail =
        typeof error === 'object' &&
        error !== null &&
        'response' in error &&
        typeof (error as { response?: unknown }).response === 'object' &&
        (error as { response?: { data?: { detail?: string } } }).response?.data?.detail
      message.error(detail || 'Ошибка массового применения видимости')
    } finally {
      setIsApplying(false)
    }
  }

  return (
    <div>
      <Breadcrumb
        items={[
          { title: 'Администрирование' },
          { title: 'Назначение дашбордов' },
        ]}
        style={{ marginBottom: 16 }}
      />

      <Row justify="space-between" align="middle" gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col>
          <Space direction="vertical" size={4}>
            <Space align="center">
              <Title level={4} style={{ margin: 0 }}>
                Рабочее место назначения дашбордов
              </Title>
              <Tag color="processing">Экспериментальный интерфейс</Tag>
            </Space>
            <Paragraph type="secondary" style={{ margin: 0, maxWidth: 900 }}>
              Контур по группам для сравнения и массового применения видимости дашбордов.
              Классический раздел «Группы доступа к дашбордам» остается основным CRUD-контуром.
            </Paragraph>
          </Space>
        </Col>
        <Col>
          <Space wrap>
            <Button onClick={() => navigate('/admin/access-groups')}>
              Классические группы
            </Button>
            <Button onClick={() => navigate('/admin/access-workspace')}>
              По сотруднику
            </Button>
          </Space>
        </Col>
      </Row>

      {workspaceOrgLabel ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={`Контекст ЛПУ: ${workspaceOrgLabel}`}
          description="Этот экран открыт как глубокая настройка видимости BI. Вернуться к общей сводке выбранного ЛПУ можно одной кнопкой."
          action={
            <Button size="small" onClick={() => navigate(biCenterUrl)}>
              В BI-центр
            </Button>
          }
        />
      ) : null}

      <Row gutter={[16, 16]} align="stretch">
        <Col xs={24} xl={8}>
          <Card
            title="Источники видимости"
            size="small"
            style={{ height: '100%' }}
            extra={
              <Segmented<SourceMode>
                size="small"
                value={sourceMode}
                options={[
                  { label: 'Группы', value: 'group' },
                  { label: 'Шаблоны', value: 'template' },
                ]}
                onChange={(value) => {
                  setSourceMode(value)
                  setSourceGroupId(null)
                  setSourceTemplateId(null)
                  setTargetOrgIds([])
                  setTargetGroupIds([])
                  setDetailsGroupId(null)
                }}
              />
            }
          >
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              {isSuperadmin ? (
                <Select
                  allowClear
                  showSearch
                  placeholder="Организация"
                  value={orgFilter}
                  options={orgOptions}
                  style={{ width: '100%' }}
                  onChange={(value) => {
                    setOrgFilter(value)
                    setSourceGroupId(null)
                    setSourceTemplateId(null)
                    setTargetOrgIds([])
                    setTargetGroupIds([])
                    setDetailsGroupId(null)
                  }}
                  filterOption={(input, option) =>
                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) ?? false
                  }
                />
              ) : null}

              <Input
                allowClear
                prefix={<SearchOutlined />}
                placeholder="Поиск по названию или ключу роли"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />

              <div style={{ maxHeight: 760, overflowY: 'auto', paddingRight: 4 }}>
                {sourceMode === 'group' ? (
                  <List
                    loading={groupsLoading}
                    locale={{ emptyText: 'Нет доступных групп видимости' }}
                    dataSource={filteredGroups}
                    renderItem={(group) => {
                      const selected = group.id === sourceGroupId
                      return (
                        <List.Item
                          onClick={() => {
                            setSourceGroupId(group.id)
                            setSourceTemplateId(null)
                            setTargetOrgIds([])
                            setTargetGroupIds([])
                            setDetailsGroupId(null)
                          }}
                          style={{
                            cursor: 'pointer',
                            marginBottom: 8,
                            padding: 14,
                            borderRadius: 12,
                            border: selected ? '1px solid #1677ff' : '1px solid #f0f0f0',
                            background: selected ? '#e6f4ff' : '#fff',
                          }}
                        >
                          <List.Item.Meta
                            avatar={<TeamOutlined style={{ fontSize: 18 }} />}
                            title={
                              <Space size={[8, 8]} wrap>
                                <Text strong>{group.name}</Text>
                                {selected ? <Tag color="processing">Источник</Tag> : null}
                              </Space>
                            }
                            description={
                              <Space size={[8, 8]} wrap>
                                <Text code>{group.zitadel_role_key}</Text>
                                <Text type="secondary">{group.member_count} участников</Text>
                              </Space>
                            }
                          />
                        </List.Item>
                      )
                    }}
                  />
                ) : (
                  <List
                    loading={templatesLoading}
                    locale={{ emptyText: 'Нет сохраненных шаблонов видимости' }}
                    dataSource={filteredTemplates}
                    renderItem={(template) => {
                      const selected = template.id === sourceTemplateId
                      return (
                        <List.Item
                          onClick={() => {
                            setSourceTemplateId(template.id)
                            setSourceGroupId(null)
                            setTargetOrgIds([])
                            setTargetGroupIds([])
                            setDetailsGroupId(null)
                          }}
                          style={{
                            cursor: 'pointer',
                            marginBottom: 8,
                            padding: 14,
                            borderRadius: 12,
                            border: selected ? '1px solid #1677ff' : '1px solid #f0f0f0',
                            background: selected ? '#e6f4ff' : '#fff',
                          }}
                        >
                          <List.Item.Meta
                            avatar={<FolderOpenOutlined style={{ fontSize: 18 }} />}
                            title={
                              <Space size={[8, 8]} wrap>
                                <Text strong>{template.name}</Text>
                                {selected ? <Tag color="processing">Источник</Tag> : null}
                              </Space>
                            }
                            description={
                              <Space size={[8, 8]} wrap>
                                <Text type="secondary">{template.entry_count} объектов</Text>
                                {template.description ? <Text type="secondary">{template.description}</Text> : null}
                              </Space>
                            }
                          />
                        </List.Item>
                      )
                    }}
                  />
                )}
              </div>
            </Space>
          </Card>
        </Col>

        <Col xs={24} xl={16}>
          {sourceLabel ? (
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <Row gutter={[16, 16]}>
                <Col xs={24} md={8}>
                  <Card size="small">
                    <Statistic title="Источник" value={sourceLabel} prefix={<FolderOpenOutlined />} />
                  </Card>
                </Col>
                <Col xs={24} md={8}>
                  <Card size="small">
                    <Statistic title="Дашбордов в источнике" value={sourceEntries.length} prefix={<AppstoreOutlined />} />
                  </Card>
                </Col>
                <Col xs={24} md={8}>
                  <Card size="small">
                    <Statistic title="Целевых групп" value={targetGroups.length} prefix={<CopyOutlined />} />
                  </Card>
                </Col>
              </Row>

              <Card
                size="small"
                title="Источник и режим применения"
                extra={
                  <Space>
                    {sourceMode === 'group' && sourceGroup ? (
                      <Button
                        icon={<SaveOutlined />}
                        onClick={() => {
                          setTemplateModalMode('create')
                          templateForm.setFieldsValue({
                            name: `${sourceGroup.name} — шаблон`,
                            description: sourceGroup.zitadel_role_key,
                          })
                          setIsTemplateModalOpen(true)
                        }}
                      >
                        Сохранить как шаблон
                      </Button>
                    ) : null}
                    {sourceMode === 'template' && sourceTemplate ? (
                      <Button
                        icon={<EditOutlined />}
                        onClick={() => {
                          setTemplateModalMode('edit')
                          templateForm.setFieldsValue({
                            name: sourceTemplate.name,
                            description: sourceTemplate.description || undefined,
                          })
                          setIsTemplateModalOpen(true)
                        }}
                      >
                        Редактировать шаблон
                      </Button>
                    ) : null}
                    {sourceMode === 'template' && sourceTemplate ? (
                      <Button danger icon={<DeleteOutlined />} onClick={() => void handleDeleteTemplate()}>
                        Удалить шаблон
                      </Button>
                    ) : null}
                    <Radio.Group value={applyMode} onChange={(event) => setApplyMode(event.target.value)}>
                      <Radio.Button value="merge">Добавить</Radio.Button>
                      <Radio.Button value="replace">Заменить</Radio.Button>
                    </Radio.Group>
                    <Button
                      type="primary"
                      icon={<SyncOutlined />}
                      onClick={() => void handleApply()}
                      disabled={targetGroups.length === 0}
                      loading={isApplying}
                    >
                      Применить
                    </Button>
                  </Space>
                }
              >
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <Select
                    mode="multiple"
                    allowClear
                    showSearch
                    placeholder="Целевые группы"
                    value={targetGroupIds}
                    options={targetOptions}
                    onChange={(value) => setTargetGroupIds(value)}
                    style={{ width: '100%' }}
                    filterOption={(input, option) =>
                      (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) ?? false
                    }
                  />
                  {isSuperadmin ? (
                    <Select
                      mode="multiple"
                      allowClear
                      showSearch
                      placeholder="Ограничить цели по организациям"
                      value={targetOrgIds}
                      options={orgOptions}
                      onChange={(value) => {
                        setTargetOrgIds(value)
                        if (value.length === 0) {
                          return
                        }
                        setTargetGroupIds((current) =>
                          current.filter((groupId) =>
                            filteredGroups.some((group) => group.id === groupId && group.org_id && value.includes(group.org_id))
                          )
                        )
                      }}
                      style={{ width: '100%' }}
                      filterOption={(input, option) =>
                        (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) ?? false
                      }
                    />
                  ) : null}
                  <Space wrap>
                    {sourceRoleFamily ? (
                      <Button
                        size="small"
                        onClick={() => setTargetGroupIds(matchingFamilyTargets.map((group) => group.id))}
                      >
                        Выбрать все группы `{sourceRoleFamily}`
                      </Button>
                    ) : null}
                    <Button size="small" onClick={() => setTargetGroupIds([])}>
                      Очистить выбор
                    </Button>
                    {sourceRoleFamily ? (
                      <Text type="secondary">
                        Совпадающий тип групп: <Text code>{sourceRoleFamily}</Text> · групп: {matchingFamilyTargets.length}
                      </Text>
                    ) : null}
                  </Space>
                  <Text type="secondary">
                    Добавить сохраняет текущие дашборды и добавляет недостающие. Заменить приводит целевые группы к точному составу источника.
                  </Text>
                  {sourceSubtitle ? <Text type="secondary">Источник: {sourceSubtitle}</Text> : null}
                </Space>
              </Card>

              {sourceRoleFamily ? (
                <Card size="small" title={`Покрытие по типу групп: ${sourceRoleFamily}`}>
                  <Table<OrgCoverageRow>
                    size="small"
                    rowKey={(row) => row.orgId}
                    columns={orgCoverageColumns}
                    dataSource={orgCoverageRows}
                    pagination={false}
                    locale={{ emptyText: 'Нет целевых организаций с совпадающим типом групп' }}
                  />
                </Card>
              ) : null}

              <Card size="small" title="Состав источника">
                <Table<WorkspaceEntry>
                  size="small"
                  loading={sourceEntriesLoading}
                  rowKey={(entry) => entry.id}
                  columns={sourceColumns}
                  dataSource={sourceEntries}
                  pagination={false}
                  locale={{ emptyText: 'В группе-источнике нет дашбордов' }}
                />
              </Card>

              <Card size="small" title="Предпросмотр изменений по целевым группам">
                {targetGroups.length === 0 ? (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Выберите хотя бы одну целевую группу" />
                ) : (
                  <Table<PreviewRow>
                    size="small"
                    rowKey={(row) => row.targetGroupId}
                    columns={previewColumns}
                    dataSource={previewRows}
                    pagination={false}
                  />
                )}
              </Card>
            </Space>
          ) : (
            <Card size="small" style={{ height: '100%' }}>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Выберите группу-источник слева, чтобы сравнивать и массово применять доступ к дашбордам"
              />
            </Card>
          )}
        </Col>
      </Row>

      <Drawer
        open={!!selectedPreviewGroup}
        onClose={() => setDetailsGroupId(null)}
        width={920}
        title={selectedPreviewGroup ? `Детальная разница: ${selectedPreviewGroup.name}` : 'Детальная разница'}
      >
        {selectedPreviewGroup ? (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Alert
              type="info"
              showIcon
              message="Предпросмотр целевой группы"
              description="Ниже точный состав дашбордов, которые будут добавлены или удалены для выбранной группы, если выполнить массовое применение."
            />

            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <Card size="small" title={`Добавится (${selectedPreviewDiff.missing.length})`}>
                  <Table<EntryDiffItem>
                    size="small"
                    rowKey={(row) => row.datalens_entry_id}
                    columns={detailColumns}
                    dataSource={selectedPreviewDiff.missing}
                    pagination={false}
                    locale={{ emptyText: 'Нет недостающих дашбордов' }}
                  />
                </Card>
              </Col>
              <Col xs={24} md={12}>
                <Card size="small" title={`Будет удалено (${selectedPreviewDiff.extra.length})`}>
                  <Table<EntryDiffItem>
                    size="small"
                    rowKey={(row) => row.datalens_entry_id}
                    columns={detailColumns}
                    dataSource={selectedPreviewDiff.extra}
                    pagination={false}
                    locale={{ emptyText: 'Нет лишних дашбордов' }}
                  />
                </Card>
              </Col>
            </Row>
          </Space>
        ) : null}
      </Drawer>

      <Modal
        open={isTemplateModalOpen}
        title={templateModalMode === 'create' ? 'Сохранить шаблон видимости' : 'Редактировать шаблон видимости'}
        okText={templateModalMode === 'create' ? 'Сохранить' : 'Обновить'}
        cancelText="Отмена"
        confirmLoading={isSavingTemplate}
        onCancel={() => setIsTemplateModalOpen(false)}
        onOk={() => void templateForm.submit()}
      >
        <Form
          form={templateForm}
          layout="vertical"
          onFinish={(values) =>
            void (templateModalMode === 'create' ? handleCreateTemplate(values) : handleUpdateTemplate(values))
          }
        >
          <Form.Item name="name" label="Название шаблона" rules={[{ required: true, message: 'Укажите название' }]}>
            <Input placeholder="Например: Администрация КБ — базовый набор" />
          </Form.Item>
          <Form.Item name="description" label="Описание">
            <Input.TextArea rows={3} placeholder="Краткое назначение или исходная группа" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
